import { DATE_FORMATS_FNS } from "components";
import { typedQueryClient, useTypedMutation } from "hooks";
import { useShowPendingChecklistsNotifications } from "modules/AppointmentDetails/hooks";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";

import type { Checklist, PIN_ORDER_STATUSES, PIN_STATUSES, PIN_TYPES, Pin, WARRANTY_TYPES } from "models";

export type AppendPinPayload = {
  pin_type_id: PIN_TYPES;
  intervention_id: number;
  appointment_id: number;
  order_status: PIN_ORDER_STATUSES | null;
  warranty_type_id: WARRANTY_TYPES;
  pin_status_id: PIN_STATUSES;
  claim_nr: string;
  ref_nr: string;
  support_nr: string;
  note: string;
  visible_important_items: boolean;
  keep_parts: boolean;
  user_id: number;
  note_attachments?: string[];
  rdw_recall_id: string;
  pon_recall_id: string;
  recalls_id: string;
  dealer_location_id: number;
  car_id: number;
};

export type AppendPinResponse = {
  pin_id: number;
  added_checklists?: Checklist[];
  not_added_checklists?: Checklist[];
};

export const useAppendPinMutation = () => {
  const { showPendingChecklistsNotifications } = useShowPendingChecklistsNotifications();

  return useTypedMutation({
    mutationFn: async (payload: AppendPinPayload) => (await ApiInstance.post("/pins/append", payload)).data,
    onSuccess: (payload, { pin_id, added_checklists, not_added_checklists }: AppendPinResponse) => {
      const appointment = typedQueryClient.getQueryData(AppointmentDetailsKeys.view);
      if (!appointment?.interventions?.length) return;

      const pin: Pin = {
        ...payload,
        id: pin_id,
        question_result_id: null,
        info: false,
        mechanic_fixed: false,
        is_dms: false,
        manufacturer_id: null,
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
      };

      const interventionIdx = appointment.interventions.findIndex(i => i.id === pin.intervention_id);
      if (interventionIdx < 0) return;

      const intervention = appointment.interventions[interventionIdx];
      const pinHistoryIdx = intervention.pin_history?.findIndex(p => p.id === pin.id) ?? -1;

      let updatedPinHistory = [...(intervention.pin_history || []), pin];
      if (pinHistoryIdx > -1 && intervention.pin_history?.length) {
        updatedPinHistory = intervention.pin_history.with(pinHistoryIdx, { ...intervention.pin_history[pinHistoryIdx], ...pin });
      }

      typedQueryClient.setQueryData(AppointmentDetailsKeys.view, {
        ...appointment,
        interventions: appointment.interventions.with(interventionIdx, { ...intervention, pin_history: updatedPinHistory })
      });

      showPendingChecklistsNotifications(added_checklists, not_added_checklists);
    }
  });
};
